import { useState } from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Loader2 } from 'lucide-react'
import { useUpdateInquiry } from '@/stores/leads.store'
import { useSystemSettings } from '@/stores/settings.store'
import { InquiryRejectReasonEnum, InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName } from '@/lib/enum-utils'
import { toast } from 'sonner'

interface RejectReasonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  inquiryId: string
  onRejected: () => void
  /**
   * Saves the rejection in place of a plain `UpdateInquiry` stage change, for
   * callers whose own caches move with the stage (the job page moves the
   * Pipeline's). Gets the same stage, reason and notes; a rejection toasts the
   * error here as usual.
   */
  onReject?: (inquiryId: string, rejectReason: InquiryRejectReasonEnum, rejectNotes: string) => Promise<unknown>
  title?: string
  description?: string
  submitLabel?: string
  /** Prefills the dialog, e.g. to edit an existing rejection's reason and notes. */
  initialReason?: InquiryRejectReasonEnum
  initialNotes?: string
  /** Button label while saving. */
  pendingLabel?: string
  /** Error toast title when the save fails. */
  errorMessage?: string
  submitVariant?: 'destructive' | 'default'
}

export function RejectReasonDialog({
  open,
  onOpenChange,
  inquiryId,
  onRejected,
  onReject,
  title = 'Reject Lead',
  description = 'Please select a reason for rejecting this lead.',
  submitLabel = 'Reject Lead',
  initialReason,
  initialNotes = '',
  pendingLabel = 'Rejecting...',
  errorMessage = 'Failed to reject lead',
  submitVariant = 'destructive',
}: RejectReasonDialogProps) {
  const prefilledReason =
    initialReason === InquiryRejectReasonEnum.INQUIRY_REJECT_REASON_UNKNOWN ? undefined : initialReason
  const [selectedReason, setSelectedReason] = useState<InquiryRejectReasonEnum | undefined>(prefilledReason)
  const [notes, setNotes] = useState(initialNotes)
  // Re-seed from the initial values each time the dialog opens, so a reopen
  // shows what is saved now rather than the last unsaved edit.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setSelectedReason(prefilledReason)
      setNotes(initialNotes)
    }
  }
  const [isRejecting, setIsRejecting] = useState(false)
  const { updateInquiryStage, isLoading: isUpdating } = useUpdateInquiry()
  const isLoading = isUpdating || isRejecting
  const { settings } = useSystemSettings()
  const rejectReasons = (settings?.inquiryRejectReasons ?? [])
    .filter(r => r !== InquiryRejectReasonEnum.INQUIRY_REJECT_REASON_UNKNOWN)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedReason(undefined)
      setNotes('')
    }
    onOpenChange(newOpen)
  }

  const handleSubmit = async () => {
    if (selectedReason === undefined) return

    try {
      setIsRejecting(true)
      if (onReject) {
        await onReject(inquiryId, selectedReason, notes)
      } else {
        await updateInquiryStage(
          inquiryId,
          InquiryStageEnum.INQUIRY_STAGE_REJECTED,
          selectedReason,
          notes,
        )
      }
      onRejected()
    } catch (error) {
      console.error('Failed to reject lead:', error)
      reportError(error, { feature: 'lead_reject', kind: 'mutation' })
      toast.error(errorMessage, {
        description: getErrorDescription(error),
      })
    } finally {
      setIsRejecting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Select
              // '' (not undefined) keeps the Select controlled while showing the placeholder.
              value={selectedReason !== undefined ? String(selectedReason) : ''}
              onValueChange={(value) => setSelectedReason(Number(value) as InquiryRejectReasonEnum)}
            >
              <SelectTrigger aria-label="Reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {rejectReasons.map((reason) => (
                  <SelectItem key={reason} value={String(reason)}>
                    {formatEnumName(reason, InquiryRejectReasonEnum)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Textarea
            placeholder="Add any additional context..."
            aria-label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedReason === undefined || isLoading}
            variant={submitVariant}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {pendingLabel}
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
